import { useEffect, useState } from 'react';

/**
 * How much of the bottom of the layout viewport the on-screen keyboard covers, in CSS pixels; 0 when
 * none (or when the browser has no `visualViewport`).
 *
 * iOS Safari, and the in-app browsers built on it, do not shrink the layout for the keyboard: a sheet
 * fixed to the bottom of the screen stays where it was, under the keys. The visual viewport does
 * shrink, so what it has lost at the bottom is what to lift such a sheet by (V6 polish, 2026-10-14:
 * variant b's question hid "Build it" and "Skip" the moment its field took focus).
 */
export function keyboardInset(): number {
  const vv = typeof window !== 'undefined' ? window.visualViewport : null;
  if (!vv) return 0;
  const covered = window.innerHeight - (vv.height + vv.offsetTop);
  // Under a few pixels is rounding and toolbars settling, not a keyboard.
  return covered > 40 ? Math.round(covered) : 0;
}

/** `keyboardInset()`, kept current while the component is on screen. */
export function useKeyboardInset(): number {
  const [inset, setInset] = useState(keyboardInset);
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    const update = (): void => setInset(keyboardInset());
    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    update();
    return () => {
      vv.removeEventListener('resize', update);
      vv.removeEventListener('scroll', update);
    };
  }, []);
  return inset;
}
